import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, TrendingDown, DollarSign, Target, Award,
  BarChart3, PieChart, Users, Calendar, ArrowUpRight, ArrowDownRight,
  RefreshCw, Trophy, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-AE', { 
    style: 'currency', 
    currency: 'AED',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const DealAnalytics = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [period, setPeriod] = useState('30');
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('month');

  useEffect(() => {
    fetchAnalytics();
    fetchTrends();
    fetchLeaderboard();
  }, [period, leaderboardPeriod]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/deals/analytics?token=${token}&days=${period}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const response = await axios.get(`${API}/deals/analytics/trends?token=${token}&months=6`);
      setTrends(response.data.trends || []);
    } catch (error) {
      console.error('Failed to fetch trends', error);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/deals/analytics/leaderboard?token=${token}&period=${leaderboardPeriod}`);
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error('Failed to fetch leaderboard', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <RefreshCw className="animate-spin text-slate-400" size={24} />
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const forecast = analytics?.forecast || {};
  const stageBreakdown = analytics?.stage_breakdown || {};
  const recentPerformance = analytics?.recent_performance || {};
  const topDeals = analytics?.top_deals || [];

  return (
    <div className="space-y-6" data-testid="deal-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-heading font-bold text-primary flex items-center gap-2">
            <BarChart3 size={24} />
            Deal Analytics
          </h3>
          <p className="text-sm text-slate-500 mt-1">Pipeline performance and insights</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[140px]" data-testid="period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4" data-testid="metric-pipeline-value">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-500">Pipeline Value</p>
              <p className="text-2xl font-bold text-primary mt-1">
                {formatCurrency(summary.pipeline_value || 0)}
              </p>
              <p className="text-xs text-slate-400 mt-1">{summary.open_deals || 0} open deals</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="text-blue-600" size={20} />
            </div>
          </div>
        </Card>

        <Card className="p-4" data-testid="metric-won-value">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-500">Won Value</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {formatCurrency(summary.won_value || 0)}
              </p>
              <p className="text-xs text-slate-400 mt-1">{summary.won_deals || 0} deals won</p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="text-green-600" size={20} />
            </div>
          </div>
        </Card>

        <Card className="p-4" data-testid="metric-win-rate">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-500">Win Rate</p>
              <p className="text-2xl font-bold text-primary mt-1">
                {summary.win_rate || 0}%
              </p>
              <Progress value={summary.win_rate || 0} className="h-2 mt-2" />
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <Target className="text-purple-600" size={20} />
            </div>
          </div>
        </Card>

        <Card className="p-4" data-testid="metric-avg-deal">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-500">Avg Deal Size</p>
              <p className="text-2xl font-bold text-primary mt-1">
                {formatCurrency(summary.avg_won_size || summary.avg_deal_size || 0)}
              </p>
              <p className="text-xs text-slate-400 mt-1">{summary.total_deals || 0} total deals</p>
            </div>
            <div className="p-2 bg-orange-100 rounded-lg">
              <Award className="text-orange-600" size={20} />
            </div>
          </div>
        </Card>
      </div>

      {/* Forecast & Stage Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Forecast */}
        <Card className="p-5" data-testid="forecast-card">
          <h4 className="font-semibold text-primary flex items-center gap-2 mb-4">
            <Sparkles size={18} />
            Revenue Forecast
          </h4>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Best Case</span>
              <span className="font-semibold text-green-600">{formatCurrency(forecast.best_case || 0)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Weighted Pipeline</span>
              <span className="font-semibold text-primary">{formatCurrency(forecast.weighted_pipeline || 0)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Conservative</span>
              <span className="font-semibold text-orange-600">{formatCurrency(forecast.worst_case || 0)}</span>
            </div>
            
            <div className="pt-4 border-t">
              <p className="text-xs text-slate-500">
                Weighted pipeline is calculated based on deal stage probabilities
              </p>
            </div>
          </div>
        </Card>

        {/* Stage Breakdown */}
        <Card className="p-5" data-testid="stage-breakdown-card">
          <h4 className="font-semibold text-primary flex items-center gap-2 mb-4">
            <PieChart size={18} />
            Pipeline by Stage
          </h4>
          <div className="space-y-3">
            {Object.entries(stageBreakdown).map(([stage, data]) => {
              const percentage = summary.total_deals > 0 
                ? Math.round((data.count / summary.total_deals) * 100) 
                : 0;
              const stageColors = {
                lead: 'bg-blue-500',
                qualified: 'bg-purple-500',
                proposal: 'bg-yellow-500',
                negotiation: 'bg-pink-500',
                closed_won: 'bg-green-500',
                won: 'bg-green-500',
                closed_lost: 'bg-red-500',
                lost: 'bg-red-500'
              };
              
              return (
                <div key={stage} className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${stageColors[stage] || 'bg-slate-400'}`} />
                  <div className="flex-1">
                    <div className="flex justify-between text-sm">
                      <span className="capitalize">{stage.replace('_', ' ')}</span>
                      <span className="text-slate-500">{data.count} ({percentage}%)</span>
                    </div>
                    <Progress value={percentage} className="h-1.5 mt-1" />
                  </div>
                  <span className="text-sm font-medium w-24 text-right">
                    {formatCurrency(data.value)}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Recent Performance & Top Deals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Performance */}
        <Card className="p-5" data-testid="recent-performance-card">
          <h4 className="font-semibold text-primary flex items-center gap-2 mb-4">
            <Calendar size={18} />
            Recent Performance ({recentPerformance.period_days || 30} days)
          </h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-primary">{recentPerformance.deals_created || 0}</p>
              <p className="text-xs text-slate-500">Created</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{recentPerformance.deals_won || 0}</p>
              <p className="text-xs text-slate-500">Won</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{recentPerformance.deals_lost || 0}</p>
              <p className="text-xs text-slate-500">Lost</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t flex justify-between">
            <div>
              <p className="text-xs text-slate-500">Value Won</p>
              <p className="text-lg font-semibold text-green-600">
                {formatCurrency(recentPerformance.value_won || 0)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Value Lost</p>
              <p className="text-lg font-semibold text-red-600">
                {formatCurrency(recentPerformance.value_lost || 0)}
              </p>
            </div>
          </div>
        </Card>

        {/* Top Deals */}
        <Card className="p-5" data-testid="top-deals-card">
          <h4 className="font-semibold text-primary flex items-center gap-2 mb-4">
            <TrendingUp size={18} />
            Top Open Deals
          </h4>
          <div className="space-y-3">
            {topDeals.length > 0 ? topDeals.map((deal, idx) => (
              <div key={deal.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg">
                <span className="text-lg font-bold text-slate-300 w-6">#{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{deal.title}</p>
                  <p className="text-xs text-slate-500 capitalize">{deal.stage?.replace('_', ' ')}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-primary">{formatCurrency(deal.value)}</p>
                  <Badge variant="outline" className="text-xs">
                    {deal.probability}% likely
                  </Badge>
                </div>
              </div>
            )) : (
              <p className="text-sm text-slate-500 text-center py-4">No open deals</p>
            )}
          </div>
        </Card>
      </div>

      {/* Leaderboard */}
      <Card className="p-5" data-testid="leaderboard-card">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-primary flex items-center gap-2">
            <Trophy size={18} />
            Sales Leaderboard
          </h4>
          <Select value={leaderboardPeriod} onValueChange={setLeaderboardPeriod}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {leaderboard.length > 0 ? (
          <div className="space-y-3">
            {leaderboard.map((person, idx) => (
              <div 
                key={person.user_id} 
                className={`flex items-center gap-4 p-3 rounded-lg ${
                  idx === 0 ? 'bg-yellow-50 border border-yellow-200' :
                  idx === 1 ? 'bg-slate-100' :
                  idx === 2 ? 'bg-orange-50' : 'bg-white'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  idx === 0 ? 'bg-yellow-400 text-white' :
                  idx === 1 ? 'bg-slate-400 text-white' :
                  idx === 2 ? 'bg-orange-400 text-white' : 'bg-slate-200'
                }`}>
                  {idx < 3 ? (
                    <Trophy size={18} />
                  ) : (
                    <span className="font-bold">{idx + 1}</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium">{person.name}</p>
                  <p className="text-xs text-slate-500">{person.deals_won} deals won</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary">{formatCurrency(person.total_value)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-8">
            No sales data for this period
          </p>
        )}
      </Card>

      {/* Trends Chart (Simple) */}
      {trends.length > 0 && (
        <Card className="p-5" data-testid="trends-card">
          <h4 className="font-semibold text-primary flex items-center gap-2 mb-4">
            <BarChart3 size={18} />
            Monthly Trends
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Month</th>
                  <th className="text-right py-2 px-3">Created</th>
                  <th className="text-right py-2 px-3">Won</th>
                  <th className="text-right py-2 px-3">Total Value</th>
                  <th className="text-right py-2 px-3">Won Value</th>
                </tr>
              </thead>
              <tbody>
                {trends.map(t => (
                  <tr key={t.month} className="border-b hover:bg-slate-50">
                    <td className="py-2 px-3 font-medium">{t.month}</td>
                    <td className="py-2 px-3 text-right">{t.deals_created}</td>
                    <td className="py-2 px-3 text-right text-green-600">{t.deals_won}</td>
                    <td className="py-2 px-3 text-right">{formatCurrency(t.total_value)}</td>
                    <td className="py-2 px-3 text-right text-green-600">{formatCurrency(t.value_won)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default DealAnalytics;
